import { useState, useEffect, useCallback } from 'react';
import {
  Sparkles,
  ClipboardPlus,
  Search,
  BarChart3,
  ShieldCheck,
  Rocket,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
} from 'lucide-react';
import { Autocomplete } from './ui';

interface InternalOnboardingTourProps {
  firstName: string;
  hasFullBrandAccess: boolean;
  onComplete: (preferences?: { timezone: string; emailNotifications: boolean }) => void;
  onDismiss: () => void;
}

interface SlideConfig {
  icon: React.ElementType;
  title: string;
  body: string;
}

const BASE_SLIDES: SlideConfig[] = [
  {
    icon: Sparkles,
    title: 'Welcome to Byte Quality Complaints',
    body: 'Your quality control hub for managing supplier complaints — from submission to resolution.',
  },
  {
    icon: ClipboardPlus,
    title: 'Report quality issues in seconds',
    body: 'Select a restaurant, choose a supplier, describe the issue, and attach photo evidence — all in a guided 3-step form.',
  },
  {
    icon: Search,
    title: 'Stay on top of every complaint',
    body: 'Filter by status, search by reference number, and follow the full timeline from submission to resolution.',
  },
  {
    icon: BarChart3,
    title: 'Turn data into action',
    body: 'Monitor trends, identify top issues by category, and see supplier performance at a glance.',
  },
];

const ADMIN_SLIDE: SlideConfig = {
  icon: ShieldCheck,
  title: 'You have admin access',
  body: 'Manage vendor organizations, invite suppliers, configure domain whitelists, and export complaint data across all restaurants.',
};

const FINAL_SLIDE: SlideConfig = {
  icon: Rocket,
  title: "You're all set",
  body: '',
};

function buildSlides(hasFullBrandAccess: boolean): SlideConfig[] {
  const slides = [...BASE_SLIDES];
  if (hasFullBrandAccess) {
    slides.push(ADMIN_SLIDE);
  }
  slides.push(FINAL_SLIDE);
  return slides;
}

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
];

export function InternalOnboardingTour({
  firstName,
  hasFullBrandAccess,
  onComplete,
  onDismiss,
}: InternalOnboardingTourProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [emailNotifications, setEmailNotifications] = useState(true);

  const slides = buildSlides(hasFullBrandAccess);
  const isLastSlide = currentSlide === slides.length - 1;

  // Lock body scroll while overlay is showing
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Escape to dismiss
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    },
    [onDismiss]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleNext = () => {
    if (isLastSlide) {
      onComplete({ timezone, emailNotifications });
    } else {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const slide = slides[currentSlide];
  const Icon = slide.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome tour"
      data-testid="onboarding-overlay"
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        data-testid="onboarding-card"
      >
        {/* Skip button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Skip tour"
          data-testid="onboarding-btn-skip"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress dots */}
        <div
          className="flex items-center justify-center gap-2 pt-6"
          data-testid="onboarding-progress"
        >
          {slides.map((_, index) => (
            <div
              key={index}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                index === currentSlide
                  ? 'bg-gradient-to-r from-byte-orange to-byte-crimson'
                  : index < currentSlide
                    ? 'bg-gray-400'
                    : 'bg-gray-300'
              }`}
              style={
                index === currentSlide
                  ? { background: 'linear-gradient(to right, #F97316, #DC2626)' }
                  : undefined
              }
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-8 pt-8 pb-6 text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-byte-orange via-byte-crimson to-byte-purple flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Icon className="w-10 h-10 text-white" />
          </div>

          {/* Title */}
          <h2
            className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3"
            data-testid="onboarding-title"
          >
            {currentSlide === 0 ? `Welcome, ${firstName}!` : slide.title}
          </h2>

          {/* Body text (slides 1-4) */}
          {slide.body && (
            <p className="text-gray-600 text-lg mb-2 max-w-sm mx-auto">{slide.body}</p>
          )}

          {/* Preferences form (last slide only) */}
          {isLastSlide && (
            <div className="mt-4 space-y-4 text-left" data-testid="onboarding-preferences">
              {/* Timezone */}
              <Autocomplete
                label="Time zone"
                options={COMMON_TIMEZONES.map((tz) => ({
                  value: tz,
                  label: tz.replace(/_/g, ' '),
                }))}
                value={timezone}
                onChange={(v) => setTimezone(v || timezone)}
                placeholder="Search or select time zone..."
                id="onboarding-timezone"
                data-testid="onboarding-select-timezone"
              />

              {/* Email notifications toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <p className="font-medium text-gray-900">Email notifications</p>
                  <p className="text-sm text-gray-500">Receive updates and alerts via email</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={emailNotifications}
                  onClick={() => setEmailNotifications(!emailNotifications)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                    emailNotifications ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                  data-testid="onboarding-toggle-email"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      emailNotifications ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="px-8 pb-8 flex gap-3">
          {currentSlide > 0 && (
            <button
              onClick={handleBack}
              className="flex-shrink-0 py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
              data-testid="onboarding-btn-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex-1 py-3 px-4 bg-gradient-to-r from-byte-orange to-byte-crimson text-white font-semibold rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-opacity flex items-center justify-center gap-2"
            data-testid={isLastSlide ? 'onboarding-btn-finish' : 'onboarding-btn-next'}
          >
            {isLastSlide ? (
              <>
                Finish Setup
                <Check className="w-5 h-5" />
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
